import { Button, Spin } from 'antd';
import Select from 'app/components/Select';
import { InspectionContext } from 'app/pages/BookInspection/context/InspectionContext';
import React, { useContext, useEffect, useMemo } from 'react';
import ModalEditInspectionTemplate from '../../../Modals/EditInspectionTemplate';
import { find, first } from 'lodash';
import { useFetchLinkageTemplate } from 'app/pages/BookInspection/hooks/useFetchLinkageTemplate';
import { ITemplate } from 'app/pages/TemplateManagment/context';

type IInpsectionCriteria = Partial<Omit<ITemplate, 'id'>> & {
  id?: string | number;
  inspectionOption?: string;
  isOnlyApplyThisJob?: boolean;
};

type Props = {
  inspectionCriteria: IInpsectionCriteria;
  productId: number;
  onChange: (value: any) => void;
};

const LinkedTemplate: React.FC<Props> = ({
  inspectionCriteria,
  productId,
  onChange
}) => {
  const [selectedTemplate, setSelectedTemplate] = React.useState<
    | {
        [key: string]: any;
      }
    | string
  >();
  const [editedTemplate, setEditedTemplate] = React.useState<{
    productId: number;
    template: IInpsectionCriteria;
  }>();
  const [open, setOpen] = React.useState(false);

  const { templates, defaultTemplate } = useContext(InspectionContext);
  const [result, linkageTemplates, onScroll] =
    useFetchLinkageTemplate(productId);
  const { isLoading, isUninitialized } = result;

  const parseDefaultTemplateToOption = useMemo(() => {
    if (defaultTemplate) {
      return {
        value: defaultTemplate.id,
        label: `${defaultTemplate.name} (Default Template) `,
        data: defaultTemplate
      };
    }
    return null;
  }, [defaultTemplate]);

  const getLinkageTemplateOrDefault = () => {
    if (linkageTemplates.length) {
      const linkageTemplate = first(linkageTemplates);
      return linkageTemplate
        ? {
            value: linkageTemplate.id,
            label: `${linkageTemplate.name} ${
              linkageTemplate.isDefault ? '(Default Template)' : ''
            }  `,
            data: linkageTemplate
          }
        : parseDefaultTemplateToOption;
    }
    return parseDefaultTemplateToOption;
  };

  const handleInitTemplate = () => {
    if (!inspectionCriteria) {
      const template = getLinkageTemplateOrDefault();
      setSelectedTemplate(template);
      onChange(template?.data);
      return;
    }
    const linkageTemplate = find(linkageTemplates, {
      name: inspectionCriteria?.name
    });

    setSelectedTemplate(
      linkageTemplate
        ? {
            value: linkageTemplate.id,
            label: `${linkageTemplate.name} ${
              linkageTemplate.isDefault ? '(Default Template)' : ''
            }  `,
            data: linkageTemplate
          }
        : inspectionCriteria.name
    );
  };

  const handleEditTemplate = () => {
    const defaultInspectionOption = inspectionCriteria?.isOnlyApplyThisJob
      ? 'only_apply_this_job'
      : '';
    try {
      const editItem: {
        productId: number;
        template: IInpsectionCriteria;
      } = {
        productId,
        template: {
          id: ''
        }
      };
      if (typeof selectedTemplate === 'object') {
        editItem['template'] = {
          name: inspectionCriteria.name ?? selectedTemplate.data.name,
          scheme: inspectionCriteria.scheme ?? selectedTemplate.data.scheme,
          inspectionOption:
            inspectionCriteria?.inspectionOption ?? defaultInspectionOption,
          id: ''
        };
      } else {
        editItem['template'] = {
          name: inspectionCriteria.name ?? defaultTemplate.name,
          scheme: inspectionCriteria.scheme ?? defaultTemplate.scheme,
          inspectionOption:
            inspectionCriteria?.inspectionOption ?? defaultInspectionOption,
          id: ''
        };
      }
      setEditedTemplate(editItem);
      setOpen(true);
    } catch (error) {
      console.error('error', error);
    }
  };

  const handleSubmitEditTemplate = (values) => {
    onChange({
      ...inspectionCriteria,
      ...values,
      id: values.template,
      name: values.name,
      scheme: JSON.stringify(values.scheme)
    });
    setOpen(false);
  };

  useEffect(() => {
    if (isLoading) return;
    if (isUninitialized) return;
    handleInitTemplate();
  }, [inspectionCriteria, linkageTemplates]);

  return (
    <div style={{ display: 'inline-flex', gap: 8 }}>
      <Select
        options={linkageTemplates.map((template) => ({
          value: template.id,
          label: `${template.name} ${
            template.isDefault ? '(Default Template)' : ''
          }  `,
          data: template
        }))}
        loading={isLoading}
        onPopupScroll={onScroll as any}
        placeholder="Select template"
        style={{ width: 500 }}
        value={selectedTemplate}
        onChange={(_, option: any) => {
          onChange(option?.data);
        }}
      />
      <Button type="link" onClick={handleEditTemplate}>
        Edit Template
      </Button>
      {open && (
        <ModalEditInspectionTemplate
          destroyOnClose
          afterClose={() => {
            setEditedTemplate(null);
          }}
          onCancel={() => {
            setOpen(false);
          }}
          onOk={handleSubmitEditTemplate}
          selectedTemplate={editedTemplate.template as any}
          templates={templates}
          open={open}
        />
      )}
    </div>
  );
};

export default LinkedTemplate;
